import type { MainModule, MjData, MjModel } from 'mujoco-js';
import type { SMEControllerClient } from './sme-controller';
import { getPublicAssetPath } from './public-path';
import type { FrameSnapshot } from './types';

let mujocoFactory: (() => Promise<MainModule>) | null = null;

const MIN_BASE_HEIGHT = 0.02;
const MAX_BASE_HEIGHT = 0.45;
const MANUAL_LIFT_OFFSET = 0.15;
const PHANTOMX_ASSET_BASE = getPublicAssetPath('phantomx/');

interface BaseHeightOptions {
  dispatchSnapshot?: boolean;
  zeroRootVelocity?: boolean;
}

async function loadMujocoModule(): Promise<MainModule> {
  if (!mujocoFactory) {
    const module = await import('mujoco-js');
    mujocoFactory = module.default;
  }
  const factory = mujocoFactory;
  if (!factory) {
    throw new Error('Failed to resolve mujoco-js factory.');
  }
  const instance = await factory();
  if (!instance) {
    throw new Error('Received empty MuJoCo module instance.');
  }
  return instance;
}
function enumNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number') {
    return value;
  }
  if (value && typeof value === 'object') {
    const maybe = (value as { value?: unknown }).value;
    if (typeof maybe === 'number') {
      return maybe;
    }
    if (typeof (value as { valueOf?: () => unknown }).valueOf === 'function') {
      const candidate = (value as { valueOf?: () => unknown }).valueOf();
      if (typeof candidate === 'number') {
        return candidate;
      }
      const coerced = Number(candidate);
      if (!Number.isNaN(coerced)) {
        return coerced;
      }
    }
  }
  const coerced = Number(value);
  return Number.isNaN(coerced) ? fallback : coerced;
}
export interface RunnerOptions {
  xmlAsset: string;
  assetFilenames: string[];
  controlDt: number;
}

type FrameListener = (snapshot: FrameSnapshot) => void;
type JointClampRange = { min: number; max: number };

export class MuJoCoRunner {
  private mujoco?: MainModule;
  private model?: MjModel;
  private data?: MjData;
  private controller?: SMEControllerClient;
  private ctrlBuffer?: Float64Array;
  private zeroAction?: Float64Array;
  private frameSkip = 1;
  private listeners = new Set<FrameListener>();
  private clampOutputs = true;
  private jointClampRange: JointClampRange | null = null;
  private running = false;
  private rafId?: number;
  private accumulator = 0;
  private lastTimestamp = 0;
  private playbackMultiplier = 1;
  private stepCounter = 0;
  private simTime = 0;
  private baseRestHeight = 0;
  private lastAction: Float64Array | null = null;
  private manualBaseTarget: number | null = null;

  constructor(private readonly options: RunnerOptions) {}

  async initialize(controller: SMEControllerClient): Promise<void> {
    this.controller = controller;
    this.mujoco = await loadMujocoModule();
    if (!this.mujoco) {
      throw new Error('Failed to load mujoco-js module.');
    }
    await this.seedFileSystem();
    const modelPath = this.options.xmlAsset;
    this.model = this.mujoco.MjModel.loadFromXML(modelPath);
    this.data = new this.mujoco.MjData(this.model);
    this.frameSkip = Math.max(1, Math.round(this.options.controlDt / this.model.opt.timestep));
    this.ctrlBuffer = this.toFloat64Array((this.data as any).ctrl, this.model.nu);
    this.zeroAction = new Float64Array(this.model.nu).fill(0);
    this.controller.reset(0);
    this.mujoco.mj_forward(this.model, this.data);
    this.captureBaseRestHeight();
  }

  private async seedFileSystem(): Promise<void> {
    const fs = this.mujoco!.FS;
    const mountPath = '/working';
    try {
      fs.mkdir(mountPath);
    } catch (error) {
      if ((error as { code?: string }).code !== 'EEXIST') {
        throw error;
      }
    }
    fs.mount(this.mujoco!.MEMFS, { root: '.' }, mountPath);
    fs.chdir(mountPath);

    await Promise.all(this.options.assetFilenames.map(async (filename) => {
      const assetUrl = `${PHANTOMX_ASSET_BASE}${filename}`;
      const response = await fetch(assetUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch asset ${filename} (${response.status}). Run npm run sync-assets first.`);
      }
      const dest = `${mountPath}/${filename}`;
      if (filename.endsWith('.xml')) {
        const text = await response.text();
        fs.writeFile(dest, text);
      } else {
        const buffer = new Uint8Array(await response.arrayBuffer());
        fs.writeFile(dest, buffer);
      }
    }));
  }

  start(): void {
    if (this.running) {
      return;
    }
    this.running = true;
    this.lastTimestamp = performance.now();
    this.accumulator = 0;
    this.rafId = window.requestAnimationFrame(this.loop);
  }

  pause(): void {
    if (!this.running) {
      return;
    }
    this.running = false;
    if (this.rafId !== undefined) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }
  }

  reset(): void {
    if (!this.mujoco || !this.model || !this.data) {
      return;
    }
    this.controller?.reset(0);
    this.mujoco.mj_resetData(this.model, this.data);
    this.mujoco.mj_forward(this.model, this.data);
    this.manualBaseTarget = null;
    this.captureBaseRestHeight();
    this.stepCounter = 0;
    this.simTime = 0;
    if (this.zeroAction) {
      this.dispatchSnapshot(this.zeroAction);
    }
  }

  setClampOutputs(enabled: boolean): void {
    this.clampOutputs = enabled;
  }

  setJointClampRange(range: JointClampRange | null): void {
    if (!range) {
      this.jointClampRange = null;
      return;
    }
    const min = Number(range.min);
    const max = Number(range.max);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
      console.warn('Ignoring invalid joint clamp range', range);
      return;
    }
    this.jointClampRange = { min, max };
  }

  setPlaybackMultiplier(multiplier: number): void {
    this.playbackMultiplier = Math.max(0.1, multiplier);
  }

  liftBase(offset = MANUAL_LIFT_OFFSET): void {
    const safeOffset = Number.isFinite(offset) ? Math.max(offset, 0) : MANUAL_LIFT_OFFSET;
    const target = this.clampBaseHeight(this.baseRestHeight + safeOffset);
    this.manualBaseTarget = target;
    this.applyBaseHeight(target);
  }

  dropBase(): void {
    this.manualBaseTarget = null;
    this.applyBaseHeight(this.baseRestHeight);
  }

  subscribe(listener: FrameListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getBodyId(name: string): number {
    if (!this.mujoco || !this.model) {
      throw new Error('MuJoCo model not ready.');
    }
    const type = enumNumber(this.mujoco.mjtObj.mjOBJ_BODY, 1);
    return this.mujoco.mj_name2id(this.model, type, name);
  }

  getSiteId(name: string): number {
    if (!this.mujoco || !this.model) {
      throw new Error('MuJoCo model not ready.');
    }
    const type = enumNumber(this.mujoco.mjtObj.mjOBJ_SITE, 6);
    return this.mujoco.mj_name2id(this.model, type, name);
  }

  private loop = (timestamp: number) => {
    if (!this.running) {
      return;
    }
    const deltaSeconds = (timestamp - this.lastTimestamp) / 1000;
    this.lastTimestamp = timestamp;
    this.accumulator = Math.min(this.accumulator + deltaSeconds * this.playbackMultiplier, 0.5);
    while (this.accumulator >= this.options.controlDt) {
      this.stepOnce();
      this.accumulator -= this.options.controlDt;
    }
    this.rafId = window.requestAnimationFrame(this.loop);
  };

  private stepOnce(): void {
    if (!this.mujoco || !this.model || !this.data || !this.ctrlBuffer || !this.controller) {
      return;
    }
    const shouldClampWithinController = this.clampOutputs && !this.jointClampRange;
    const controllerAction = this.controller.step(shouldClampWithinController);
    const action = this.clampOutputs && this.jointClampRange
      ? this.applyJointClampRange(controllerAction)
      : controllerAction;
    this.ctrlBuffer.set(action.subarray(0, Math.min(action.length, this.ctrlBuffer.length)));
    for (let i = 0; i < this.frameSkip; i += 1) {
      this.mujoco.mj_step(this.model, this.data);
      if (this.manualBaseTarget !== null) {
        this.applyBaseHeight(this.manualBaseTarget, { dispatchSnapshot: false, zeroRootVelocity: false });
      }
    }
    this.stepCounter += 1;
    this.simTime = this.stepCounter * this.options.controlDt;
    this.dispatchSnapshot(action);
  }

  private dispatchSnapshot(action: Float64Array): void {
    this.lastAction = Float64Array.from(action);
    const snapshot = this.buildSnapshot(action);
    this.listeners.forEach((listener) => listener(snapshot));
  }

  private applyJointClampRange(action: Float64Array): Float64Array {
    const range = this.jointClampRange;
    if (!range) {
      return action;
    }
    const { min, max } = range;
    for (let i = 0; i < action.length; i += 1) {
      if (action[i] < min) {
        action[i] = min;
      } else if (action[i] > max) {
        action[i] = max;
      }
    }
    return action;
  }

  private buildSnapshot(action: Float64Array): FrameSnapshot {
    if (!this.model || !this.data) {
      throw new Error('MuJoCo state unavailable.');
    }
    const bodyPositions = this.toFloat64Array((this.data as any).xpos, this.model.nbody * 3);
    const sitePositions = this.toFloat64Array((this.data as any).site_xpos, this.model.nsite * 3);
    const bodyQuats = this.toFloat64Array((this.data as any).xquat, this.model.nbody * 4);
    const qpos = this.toFloat64Array((this.data as any).qpos, this.model.nq);
    const basePosition: [number, number, number] = [qpos[0], qpos[1], qpos[2]];
    const baseQuat: [number, number, number, number] = [qpos[3], qpos[4], qpos[5], qpos[6]];
    return {
      step: this.stepCounter,
      simTime: this.simTime,
      action: Float64Array.from(action),
      bodyPositions: Float64Array.from(bodyPositions),
      sitePositions: Float64Array.from(sitePositions),
      bodyQuats: Float64Array.from(bodyQuats),
      basePosition,
      baseQuat,
      basisState: this.controller?.basisState() ?? new Float64Array(),
      cpgState: this.controller?.cpgState() ?? new Float64Array(),
    };
  }

  private toFloat64Array(value: unknown, length: number): Float64Array {
    if (value instanceof Float64Array) {
      return value;
    }
    if (typeof value === 'number') {
      const heap = this.mujoco!.HEAPF64;
      const start = value / Float64Array.BYTES_PER_ELEMENT;
      return heap.subarray(start, start + length);
    }
    throw new Error('Unsupported MuJoCo buffer type.');
  }

  private applyBaseHeight(targetHeight: number, options: BaseHeightOptions = {}): void {
    if (!this.mujoco || !this.model || !this.data) {
      return;
    }
    const { dispatchSnapshot = true, zeroRootVelocity = true } = options;
    const qpos = this.toFloat64Array((this.data as any).qpos, this.model.nq);
    if (qpos.length < 3) {
      return;
    }
    const clamped = this.clampBaseHeight(targetHeight);
    qpos[2] = clamped;
    const qvel = this.toFloat64Array((this.data as any).qvel, this.model.nv);
    if (zeroRootVelocity) {
      for (let i = 0; i < Math.min(6, qvel.length); i += 1) {
        qvel[i] = 0;
      }
    } else if (qvel.length >= 3) {
      qvel[2] = 0;
    }
    this.mujoco.mj_forward(this.model, this.data);
    if (dispatchSnapshot) {
      const fallbackLength = this.model.nu;
      const action = this.lastAction ?? this.zeroAction ?? new Float64Array(fallbackLength).fill(0);
      this.dispatchSnapshot(action);
    }
  }

  private captureBaseRestHeight(): void {
    if (!this.data || !this.model) {
      return;
    }
    const qpos = this.toFloat64Array((this.data as any).qpos, this.model.nq);
    if (qpos.length >= 3) {
      this.baseRestHeight = qpos[2];
    }
  }

  private clampBaseHeight(target: number): number {
    return Math.min(Math.max(target, MIN_BASE_HEIGHT), MAX_BASE_HEIGHT);
  }
}
